<template>
    <KCard
        id="package-booking"
        class="package-booking"
        variant="surface"
        padding="md"
        elevation="raised"
    >
        <h2 class="package-booking__title k-heading-4">
            Plan this holiday
        </h2>
        <p class="package-booking__intro">
            Choose your options for an instant estimate.
        </p>

        <PackageQuoteForm
            :quote="quote"
            :addons="addons"
            :pricing-note="content.pricing_note"
        />

        <p
            v-if="bookingBlockReason"
            class="package-booking__note"
            role="status"
        >
            {{ bookingBlockReason }}
        </p>

        <div class="package-booking__actions">
            <KButton
                type="button"
                full-width
                :disabled="!canBook"
                label="Request via email"
                @click="emit('book')"
            />
            <KButton
                v-if="whatsappNumber"
                :as="canBook ? 'a' : 'button'"
                variant="secondary"
                full-width
                :href="canBook ? whatsappHref : undefined"
                :target="canBook ? '_blank' : undefined"
                :rel="canBook ? 'noopener' : undefined"
                :disabled="!canBook"
                label="Book via WhatsApp"
                @click="onWhatsappClick"
            >
                <template #icon-start>
                    <Icon name="tabler:brand-whatsapp" />
                </template>
            </KButton>
        </div>
    </KCard>
</template>

<script setup>
import { KButton, KCard } from '@vemedia/karibu'
import { computed } from 'vue'
import PackageQuoteForm from './package-quote-form.vue'

// The sidebar surface for the quote: the configurator plus the two ways to send it. The
// quote itself is owned by the content type, so the dialog's first step and the sticky bar
// read exactly the same numbers.
const props = defineProps({
    packageName: {
        type: String,
        required: true,
    },
    /** A `usePackageQuote()` instance, shared with every other surface on the page. */
    quote: {
        type: Object,
        required: true,
    },
    content: {
        type: Object,
        required: true,
    },
    whatsappNumber: {
        type: String,
        default: '',
    },
})

const emit = defineEmits(['book'])

const { capture } = useTracking()
const addons = props.content.addons || []

const { canBook, bookingBlockReason, total, totalGuests, summaryText } = props.quote

const whatsappHref = computed(() =>
    `https://wa.me/${props.whatsappNumber}?text=${encodeURIComponent(summaryText.value)}`,
)

function onWhatsappClick(event) {
    if (!canBook.value) {
        event?.preventDefault()
        return
    }

    capture('whatsapp_click', {
        placement: 'package-booking',
        package: props.packageName,
    })
    capture('booking_started', {
        channel: 'whatsapp',
        placement: 'package-booking',
        package: props.packageName,
        total: total.value,
        guests: totalGuests.value,
    })
}
</script>

<style lang="scss" scoped>
@use '@vemedia/bcn-components/scss/responsive' as *;

// The surface itself — background, border, radius, elevation, padding — is KCard's, and
// the configurator's is `package-quote-form.vue`'s. This block owns only what neither has
// an opinion about: where the panel sits on the page, and its own chrome.
.package-booking {
    position: static;
    scroll-margin-top: calc(var(--site-header-height) + var(--space-4));

    @include desktop {
        position: sticky;
        // Only the site navigation is sticky now, so this clears that and nothing else.
        top: calc(var(--site-header-height) + var(--space-4));
        scroll-margin-top: calc(var(--site-header-height) + var(--space-4));
    }

    &__title {
        margin-block-end: var(--space-1);
    }

    &__intro {
        margin-block-end: var(--space-4);
        color: var(--color-text-muted);
        font-size: var(--font-size-2);
    }

    &__note {
        margin-block: var(--space-2);
        color: var(--color-danger-text);
        font-size: var(--font-size-2);
    }

    &__actions {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
        margin-block-start: var(--space-4);
    }
}
</style>
